<template>
  <header
    ref="header"
    class="m-header"
    :class="{
      'flex-start': loginStatus
    }"
  >
    <div class="m-header-left">
      <button
        class="m-az-btn"
        aria-label="A-Z"
        @click="showAZmenu"
      >
        <img
          width="14px"
          height="14px"
          :src="themeImages.azIcon"
          alt="A-Z"
        >
      </button>
    </div>

    <div class="m-header-mid">
      <a
        class="m-logo"
        :href="homeUrl"
      >
        <img v-if="isChristmasLogo" src="~assets/images/logo-christmas-wap@2x.png" alt="MSport Christmas" class="custom-logo">
        <img
          v-else
          :src="themeImages.mSportLogo"
          alt="MSport"
          width="125"
          height="20"
        >
      </a>
    </div>

    <div class="m-header-right">
      <template v-if="!loginStatus">
        <div class="m-btn-group">
          <button
            class="m-register-btn m-btn"
            @click="openSignup"
          >
            Join
          </button>
          <button
            class="m-login-btn m-btn"
            @click="handleLogin"
          >
            Login
          </button>
        </div>
      </template>

      <template v-else>
        <ContainedIconButton
          class="m-my-bets"
          @click="gotoCashout"
        >
          <img
            slot="icon"
            :src="themeImages.myBetsIcon"
          >
          <span>My</span>
          <span>Bets</span>
          <span
            v-if="myBetsCount"
            class="num"
          >{{ myBetsCount }}</span>
        </ContainedIconButton>

        <ContainedIconButton
          class="m-user-btn"
          @click="handleChangeMe"
        >
          <img
            slot="icon"
            :src="themeImages.meIcon"
          >
          {{ $t('CURRENCY_UNIT') }} <span id="balance-num">{{ localShowBalance }}</span>

          <div
            v-if="messageCount && !isClicked"
            class="num"
          >
            {{ messageCount }}
          </div>

          <!-- 浮窗广告 -->
          <TopBubbleAd v-if="hasTopBubble" />
        </ContainedIconButton>
      </template>
    </div>

    <!-- AZmenu popup -->
    <VPopup
      v-model="azmenuVisible"
      class="m-az-menu"
      type="popup-left"
      @close="closeAZmenu"
    >
      <keep-alive>
        <AZmenu
          v-if="azmenuVisible"
          @close="closeAZmenu"
        />
      </keep-alive>
    </VPopup>

    <!-- Me popup -->
    <VPopup
      v-model="meVisible"
      class="m-popup-me"
      type="popup-right"
      @close="closeMe"
    >
      <keep-alive>
        <Me
          v-if="meVisible"
          @close="closeMe"
        />
      </keep-alive>
    </VPopup>
  </header>
</template>

<script>
import { computed, onMounted, getCurrentInstance } from '@vue/composition-api'
import { useEventBus } from '@vueuse/core'
import VPopup from 'fe-vui/popup'
import { REGION } from '@ms/config/app'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@ms/stores/app'
import { useAuthStore } from '@ms/stores/auth'
import { useUserStore } from '@ms/stores/user'
import { useCommonMyBetsStore } from '@ms/stores/myBets'
import { useUserStatsStore } from '@ms/stores/user/userStats'
import eventBus from '@ms/utils/event-bus.js'
import { prefixLink } from '@ms/config/app'
import { isMainRouter } from 'wap/router/adapter.js'
import asyncComponent from '@ms/components/asyncComponent.js'
// import AZmenu from 'wap/components/Azmenu'
import modalHelper from '@ms/utils/scroll-fixed'
import ContainedIconButton from './pagelet/ContainedIconButton'
import TopBubbleAd from './pagelet/TopBubbleAd.vue'
import parse from 'url-parse'
import { openSignup } from '@ms/components/signup/signup'
import 'wap/utils/dialog-login'
import { useThemeImages } from 'composables/useThemeImages.js'

const AZmenu = asyncComponent(() => import(/* webpackPrefetch: true, webpackChunkName: 'aside-azmenu' */ 'wap/components/Azmenu'))
const Me = asyncComponent(() => import(/* webpackPrefetch: true, webpackChunkName: 'aside-me' */ 'wap/components/Me'))

export default {
  name: 'LayoutHeader',
  components: {
    VPopup,
    AZmenu,
    Me,
    ContainedIconButton,
    TopBubbleAd
  },
  props: {
    // 应用来源
    source: {
      type: String,
      default: ''
    },
    // 是否有浮窗广告
    hasTopBubble: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { proxy: vm } = getCurrentInstance()

    const { isChristmasLogo } = storeToRefs(useAppStore())
    const auth = useAuthStore()
    const { loginStatus } = storeToRefs(auth)
    const commonMyBets = useCommonMyBetsStore()
    const { myBetsCount } = storeToRefs(commonMyBets)
    const user = useUserStore()
    const { phone, showBalance } = storeToRefs(user)
    const { getBalance } = user
    const userStats = useUserStatsStore()
    const { messageCount } = storeToRefs(userStats)

    const decimals = ['ng', 'ug'].includes(REGION) ? 0 : 2
    const localShowBalance = computed(() => {
      return decimals === 0 ? showBalance.value.replace(/\.\d+/, '') : showBalance.value
    })

    const themeImages = useThemeImages({
      dark: {
        default: {
          mSportLogo: require('public/images/logo/logo.png'),
          myBetsIcon: require('public/images/icon/myBets.png'),
          meIcon: require('public/images/icon/me.png'),
          azIcon: require('public/images/icon/az.png')
        }
      },
      light: {
        default: {
          mSportLogo: require('public/images/logo/logo-dark.png'),
          myBetsIcon: require('public/images/icon/myBets[light-default].png'),
          meIcon: require('public/images/icon/me[light-default].png'),
          azIcon: require('public/images/icon/az[light-default].png')
        }
      }
    })

    useEventBus('LOGIN_SUCCESS').on(useCommonMyBetsStore().getMyBetsCount)

    onMounted(() => {
      const url = new URL(location.href)
      const me = url.searchParams.get('me')

      if (me === 'open' && auth.loginStatus) {
        vm.meVisible = true
      }
    })

    return {
      isChristmasLogo,
      loginStatus,
      getBalance,
      localShowBalance,
      myBetsCount,
      phone,
      messageCount,
      themeImages
    }
  },
  data() {
    return {
      azmenuVisible: false,
      meVisible: false,
      homeUrl: prefixLink(),
      depositTip: false,
      isClicked: false
    }
  },
  created() {
    if (this.loginStatus) {
      this.getBalance().catch(console.error)
      useCommonMyBetsStore().getMyBetsCount()
      useUserStatsStore().getNewMessage()
    }
    eventBus.$on('update-balance', this.getBalance)
    eventBus.$on('close_deposit_tip', () => {
      this.depositTip = false
      this.changeMe()
    })
  },
  mounted() {
    if (this.loginStatus) {
      const { open } = parse(location.href, true).query
      if (open && open === 'me_deposit') {
        this.changeMe()
        setTimeout(() => {
          this.depositTip = true
        }, 500)
      }
      !this.phone && useUserStore().getAccountInfoWrap()

      eventBus.$on('phone-change', this.getBalance)
    }
  },
  methods: {
    showAZmenu() {
      this.meVisible = false // close me-popup
      this.azmenuVisible = true
      // 禁止外层滚动
      modalHelper.afterOpen()
      window.mstrace('Top_AZ')
    },
    closeAZmenu() {
      // 还原外层滚动
      modalHelper.beforeClose()
      this.azmenuVisible = false
    },
    openSignup() {
      openSignup()
      window.mstrace('header_join')
    },
    gotoCashout() {
      const routePath = '/my_bets/cashout'

      if (routePath && isMainRouter(this.$router)) {
        if (this.$route.fullPath.toLowerCase() === routePath.toLowerCase() || this.$route.name === 'cashout') {
          return
        }
        this.$router.push(routePath)
        return
      }
      location.href = prefixLink(routePath)
    },
    changeMe() {
      if (this.depositTip && this.meVisible) return
      if (this.meVisible) {
        // 还原外层滚动
        modalHelper.beforeClose()
        this.meVisible = false
      } else {
        // event bus to close the TopBubbleAd
        eventBus.$emit('close_topBubbleAd', true)
        this.meVisible = true
        // 禁止外层滚动
        modalHelper.afterOpen()
      }
      this.isClicked = true
    },
    handleChangeMe() {
      this.changeMe()
      window.mstrace('Top_Me')
    },
    closeMe() {
      // 还原外层滚动
      modalHelper.beforeClose()
      this.meVisible = false

      const url = new URL(location.href)
      url.searchParams.delete('me')
      history.replaceState(null, null, url.toString())
    },
    handleLogin() {
      this.$login()
      window.mstrace(`${this.source}_Login`, this.source)
    }
  }
}
</script>

<style lang="scss" scoped>
@use "wap/common/style/vars.scss" as vars;

.m-header {
  position: relative;
  display: flex;
  height: 43px;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  color: #ffca27;

  &::after {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 90px;
    background: #000 url("./images/bg-header.png") no-repeat 0 0;
    background-size: 100% 90px;
    content: "";
  }

  &.flex-start {
    .m-header-left,
    .m-header-mid {
      flex: none;
    }

    .m-header-mid {
      margin-left: 20px;
    }
  }

  .m-az-btn,
  .m-logo,
  .m-btn {
    display: block;
    white-space: nowrap;
  }

  .m-header-left {
    position: relative;
    z-index: 1;
    display: flex;
    flex: 1;
    align-items: center;
    transition: all 0.3s linear;

    .m-az-btn {
      display: flex;
      width: 24px;
      height: 24px;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: rgba(246, 246, 246, 20%);
    }
  }

  .m-header-mid {
    position: relative;
    z-index: 1;
    flex: 2;
    text-align: center;



    .m-logo {
      img {
        width: 125px;
        vertical-align: top;
      }
    }
  }

  .m-header-right {
    position: relative;
    z-index: 1;
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: flex-end;
    column-gap: 8px;
    font-size: 10px;
    line-height: 14px;

    .m-btn-group {
      display: flex;
      align-items: center;
      column-gap: 4px;

      .m-btn {
        width: 40px;
        height: 24px;
        border-radius: 8px;
        font-size: 12px;

        &.m-register-btn {
          background: #ffca27;
          color: #000;
          font-family: Roboto;
          font-weight: 700;
        }

        &.m-login-btn {
          background: rgba(246, 246, 246, 20%);
          color: #ffca27;
          font-family: Helvetica;
        }
      }
    }

    .m-my-bets {
      position: relative;

      .num {
        background: #666;
        color: vars.$primary;
      }
    }

    .num {
      position: absolute;
      top: 0;
      right: 0;
      height: 14px;
      padding: 0 4px;
      border-radius: 7px;
      font-family: Roboto, sans-serif;
      font-size: 10px;
      line-height: 14px;
      text-align: center;
      transform: translate(50%, -50%);
    }

    .m-user-btn {
      position: relative;
      min-width: 52px;

      #balance-num {
        font-weight: bold;
      }

      & .num {
        background: vars.$red;
      }
    }
  }

  .m-az-menu {
    position: fixed;
    z-index: 2022;
    top: 0;
    left: 0;
    color: #000;

    .popup-wrap {
      width: calc(290/360*100%);
      max-width: 600px;
      background-color: #000;

      .close-btn {
        top: 14px;
        right: 0;
        width: 38px;

        svg {
          width: 16px;
          fill: #fff;
        }
      }
    }

    .snap-nav {
      max-width: 750px;
    }
  }

  .m-popup-me {
    z-index: 2000!important;

    ::v-deep .popup-wrap {
      overflow: auto;
      width: calc(310/360*100%);
      max-width: 600px;
      background: #1b1b1b;

      & > .close-btn {
        top: 0;
        right: 0;
        width: auto;
        height: 42px;
        padding-right: 20px;
        padding-left: 9px;
      }
    }
  }
}

@media screen and (max-width: 350px) {
  .m-header {
    &.flex-start {
      .m-header-mid {
        margin-left: 2px;
      }
    }
  }
}

[data-mode="light"] .m-header {
  color: #000;

  &::after {
    background: linear-gradient(
      180deg,
      #FFCA27 0%,
      #f8f8f8 50%,
      #f8f8f8 100%,
    );
  }

  .m-header-left {
    .m-az-btn {
      background: rgba(255,255,255,70%);
    }
  }

  .m-header-right {
    .m-btn-group {
      .m-btn {
        &.m-register-btn {
          background: #FF7D1E;
          color: #fff;
        }

        &.m-login-btn {
          background: rgba(255,255,255,70%);
          color: #000;
        }
      }
    }

    .m-my-bets {
      .num {
        color: #ccc;
      }
    }

    .m-user-btn {
      .num {
        background: #666;
        color: #ccc;
      }
    }
  }

  .m-popup-me {
    z-index: 2000!important;

    ::v-deep .popup-wrap {
      background: #f8f8f8;
    }
  }
}
</style>
